import { API_BASE_URL } from '../config';

// Storage Manager Utility for handling backend storage with localStorage fallback
const storageManager = {
  // Maximum size for a single item (5MB) - for localStorage fallback only
  MAX_ITEM_SIZE: 5 * 1024 * 1024,

  // Maximum total storage (10MB) - for localStorage fallback only
  MAX_TOTAL_STORAGE: 10 * 1024 * 1024,

  // Rate limiting and caching
  _pendingRequests: new Map(),
  _requestCache: new Map(),
  _lastRequestTime: new Map(),
  _minRequestInterval: 2000, // Minimum 2 seconds between requests for same job

  estimatePayloadBytes(value) {
    try {
      return new Blob([JSON.stringify(value)]).size;
    } catch (error) {
      return 0;
    }
  },

  // Sanitize heavy candidate payloads before storage to reduce DB size/cost.
  sanitizeContactHistory(contactHistory) {
    if (!contactHistory || typeof contactHistory !== 'object') return null;

    const raw = contactHistory.raw_communication_data || {};
    const takeLast = (arr, n = 3) => (Array.isArray(arr) ? arr.slice(-n) : []);
    const trim = (value, max = 140) => {
      const str = value == null ? '' : String(value);
      return str.length > max ? `${str.slice(0, max)}...` : str;
    };

    const slimRaw = {
      stats: raw.stats
        ? {
            lastContactDate: raw.stats.lastContactDate || null,
            lastContactDirection: raw.stats.lastContactDirection || null,
            totalInteractions: raw.stats.totalInteractions || 0
          }
        : undefined,
      calls: raw.calls
        ? {
            totalCalls: raw.calls.totalCalls || 0,
            lastCallDate: raw.calls.lastCallDate || null,
            history: takeLast(raw.calls.history).map((c) => ({
              timestamp: c.timestamp || null,
              status: c.status || null,
              type: c.type || null,
              duration: c.duration || 0,
              jobId: c.jobId || null
            }))
          }
        : undefined,
      email: raw.email
        ? {
            totalEmails: raw.email.totalEmails || 0,
            lastContactDate: raw.email.lastContactDate || null,
            history: takeLast(raw.email.history).map((e) => ({
              timestamp: e.timestamp || null,
              status: e.status || null,
              subject: trim(e.subject, 100),
              jobId: e.jobId || null
            }))
          }
        : undefined,
      whatsapp: raw.whatsapp
        ? {
            totalMessages: raw.whatsapp.totalMessages || 0,
            outboundMessages: raw.whatsapp.outboundMessages || 0,
            lastMessageDate: raw.whatsapp.lastMessageDate || null,
            history: takeLast(raw.whatsapp.history).map((w) => ({
              timestamp: w.timestamp || null,
              status: w.status || null,
              direction: w.direction || null,
              jobId: w.jobId || null,
              phoneNumber: w.phoneNumber || null,
              messageId: w.messageId || null,
              content_preview: trim(w.content, 120)
            }))
          }
        : undefined
    };

    return {
      contacted: Boolean(contactHistory.contacted),
      lastContactDate: contactHistory.lastContactDate || null,
      lastContactMethod: contactHistory.lastContactMethod || null,
      lastJobId: contactHistory.lastJobId || null,
      lastJobTitle: contactHistory.lastJobTitle || null,
      raw_communication_data: slimRaw
    };
  },

  sanitizeResultForStorage(result) {
    if (!result || typeof result !== 'object') return result;

    const trim = (value, max = 12000) => {
      const str = value == null ? '' : String(value);
      return str.length > max ? `${str.slice(0, max)}...` : str;
    };

    const sanitizeArray = (arr, maxItems = 8) => (Array.isArray(arr) ? arr.slice(0, maxItems) : []);

    const workExperience = sanitizeArray(result.workExperience, 6).map((w) => ({
      title: w?.title || '',
      company: w?.company || '',
      start_date: w?.start_date || '',
      end_date: w?.end_date || '',
      description: trim(w?.description, 400)
    }));

    const education = sanitizeArray(result.education, 4).map((e) => ({
      degree: e?.degree || '',
      institution: e?.institution || '',
      start_year: e?.start_year || '',
      end_year: e?.end_year || ''
    }));

    return {
      id: result.id,
      name: result.name || '',
      contactName: result.contactName || '',
      title: result.title || result.job_title || '',
      email: result.email || '',
      phoneNumbers: Array.isArray(result.phoneNumbers) ? result.phoneNumbers.slice(0, 3) : [],
      location: result.location || null,
      photo: result.photo || null,
      linkedinUrl: result.linkedinUrl || null,
      score: result.score,
      combined_score: result.combined_score,
      similarity: result.similarity,
      keyword_match_count: result.keyword_match_count,
      keyword_matches: result.keyword_matches,
      excerpt: trim(result.excerpt || result.text_excerpt || '', 3000),
      fullText: trim(result.fullText || result.full_text || '', 12000),
      matchingContext: result.matchingContext || null,
      rating: result.rating || null,
      workExperience,
      education,
      contactHistory: this.sanitizeContactHistory(result.contactHistory)
    };
  },

  sanitizeResultsForStorage(results) {
    if (!Array.isArray(results)) return results;
    return results.map((r) => this.sanitizeResultForStorage(r));
  },

  // Check if we should skip this request (debouncing)
  _shouldSkipRequest(key) {
    const lastTime = this._lastRequestTime.get(key);
    const now = Date.now();

    if (lastTime && (now - lastTime) < this._minRequestInterval) {
      console.log(`Skipping request for ${key} - too soon (${now - lastTime}ms since last request)`);
      return true;
    }

    return false;
  },

  // Update request tracking
  _updateRequestTime(key) {
    this._lastRequestTime.set(key, Date.now());
  },

  // Backend Storage Methods (Primary) - No localStorage fallback
  async storeQueryResults(jobId, results) {
    const requestKey = `query_${jobId}`;

    // Check if we should skip this request (rate limiting)
    if (this._shouldSkipRequest(requestKey)) {
      console.log(`Rate limit: Skipping store request for job ${jobId}`);
      // Return cached result if available
      const cached = this._requestCache.get(requestKey);
      if (cached) {
        return cached;
      }
      // Return a success response without making the request
      return { success: true, message: 'Skipped due to rate limiting' };
    }

    // Check if there's already a pending request for this job
    if (this._pendingRequests.has(requestKey)) {
      console.log(`Request already pending for job ${jobId}, waiting...`);
      return this._pendingRequests.get(requestKey);
    }

    // Create the request promise
    const requestPromise = (async () => {
      try {
        this._updateRequestTime(requestKey);

        const originalBytes = this.estimatePayloadBytes(results);
        const sanitizedResults = this.sanitizeResultsForStorage(results);
        const sanitizedBytes = this.estimatePayloadBytes(sanitizedResults);
        console.log(
          `[storageManager] Query results payload reduced for ${jobId}: ${originalBytes}B -> ${sanitizedBytes}B`
        );
        const response = await fetch(`${API_BASE_URL}/api/storage/query-results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            job_id: jobId,
            results: sanitizedResults
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Backend storage - Query results stored:', result);

          // Log chunking information if document was chunked
          if (result.chunked) {
            console.log(`Document was chunked into ${result.total_chunks} chunks for job ${jobId}`);
          }

          // Cache the result
          this._requestCache.set(requestKey, result);

          return result;
        } else if (response.status === 429) {
          console.warn(`Rate limit hit for job ${jobId}. Storing locally instead.`);
          // Silently store in localStorage as fallback
          this.storeData(`queryResults_${jobId}`, sanitizedResults);
          return { success: true, message: 'Stored locally due to rate limit' };
        } else {
          throw new Error(`Backend storage failed: ${response.status} - ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error storing query results in backend:', error);
        // Don't throw - fail gracefully and store locally
        const sanitizedResults = this.sanitizeResultsForStorage(results);
        this.storeData(`queryResults_${jobId}`, sanitizedResults);
        return { success: true, message: 'Stored locally due to error', error: error.message };
      } finally {
        // Remove from pending requests
        this._pendingRequests.delete(requestKey);
      }
    })();

    // Store the pending request
    this._pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  },

  async getQueryResults(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/query-results/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          // Handle chunked documents
          if (result.chunked) {
            console.log(`Retrieved chunked document for job ${jobId}`);
          }

          return result.data;
        } else {
          console.warn('No query results found for job:', jobId);
          return null;
        }
      } else {
        throw new Error(`Backend retrieval failed: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error getting query results from backend:', error);
      throw new Error(`Backend retrieval unavailable: ${error.message}`);
    }
  },

  async storeEvaluationResults(jobId, evaluations) {
    const requestKey = `evaluation_${jobId}`;

    // Check if we should skip this request (rate limiting)
    if (this._shouldSkipRequest(requestKey)) {
      console.log(`Rate limit: Skipping evaluation store request for job ${jobId}`);
      const cached = this._requestCache.get(requestKey);
      if (cached) return cached;
      return { success: true, message: 'Skipped due to rate limiting' };
    }

    // Check if there's already a pending request
    if (this._pendingRequests.has(requestKey)) {
      console.log(`Evaluation request already pending for job ${jobId}, waiting...`);
      return this._pendingRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      try {
        this._updateRequestTime(requestKey);

        const response = await fetch(`${API_BASE_URL}/api/storage/evaluation-results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            job_id: jobId,
            evaluations: evaluations
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Backend storage - Evaluation results stored:', result);

          if (result.chunked) {
            console.log(`Document was chunked into ${result.total_chunks} chunks for job ${jobId}`);
          }

          this._requestCache.set(requestKey, result);
          return result;
        } else if (response.status === 429) {
          console.warn(`Rate limit hit for evaluation job ${jobId}. Storing locally instead.`);
          this.storeData(`evaluationResults_${jobId}`, evaluations);
          return { success: true, message: 'Stored locally due to rate limit' };
        } else {
          throw new Error(`Backend storage failed: ${response.status} - ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error storing evaluation results in backend:', error);
        this.storeData(`evaluationResults_${jobId}`, evaluations);
        return { success: true, message: 'Stored locally due to error', error: error.message };
      } finally {
        this._pendingRequests.delete(requestKey);
      }
    })();

    this._pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  },

  async getEvaluationResults(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/evaluation-results/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          // Handle chunked documents
          if (result.chunked) {
            console.log(`Retrieved chunked evaluation document for job ${jobId}`);
          }

          return result.data;
        } else {
          console.warn('No evaluation results found for job:', jobId);
          return null;
        }
      } else {
        throw new Error(`Backend retrieval failed: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error getting evaluation results from backend:', error);
      throw new Error(`Backend retrieval unavailable: ${error.message}`);
    }
  },

  async storeCallResults(jobId, calls) {
    const requestKey = `call_${jobId}`;

    // Check if we should skip this request (rate limiting)
    if (this._shouldSkipRequest(requestKey)) {
      console.log(`Rate limit: Skipping call store request for job ${jobId}`);
      const cached = this._requestCache.get(requestKey);
      if (cached) return cached;
      return { success: true, message: 'Skipped due to rate limiting' };
    }

    // Check if there's already a pending request
    if (this._pendingRequests.has(requestKey)) {
      console.log(`Call request already pending for job ${jobId}, waiting...`);
      return this._pendingRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      try {
        this._updateRequestTime(requestKey);

        const response = await fetch(`${API_BASE_URL}/api/storage/call-results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            job_id: jobId,
            calls: calls
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Backend storage - Call results stored:', result);

          if (result.chunked) {
            console.log(`Document was chunked into ${result.total_chunks} chunks for job ${jobId}`);
          }

          this._requestCache.set(requestKey, result);
          return result;
        } else if (response.status === 429) {
          console.warn(`Rate limit hit for call job ${jobId}. Storing locally instead.`);
          this.storeData(`callResults_${jobId}`, calls);
          return { success: true, message: 'Stored locally due to rate limit' };
        } else {
          throw new Error(`Backend storage failed: ${response.status} - ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error storing call results in backend:', error);
        this.storeData(`callResults_${jobId}`, calls);
        return { success: true, message: 'Stored locally due to error', error: error.message };
      } finally {
        this._pendingRequests.delete(requestKey);
      }
    })();

    this._pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  },

  async getCallResults(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/call-results/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          // Handle chunked documents
          if (result.chunked) {
            console.log(`Retrieved chunked call document for job ${jobId}`);
          }

          return result.data;
        } else {
          console.warn('No call results found for job:', jobId);
          return null;
        }
      } else {
        throw new Error(`Backend retrieval failed: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error getting call results from backend:', error);
      throw new Error(`Backend retrieval unavailable: ${error.message}`);
    }
  },

  async clearJobData(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/jobs/${jobId}/all-data`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Backend storage - Job data cleared:', result);
        return result;
      } else {
        throw new Error(`Backend cleanup failed: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error clearing job data in backend:', error);
      throw new Error(`Backend cleanup unavailable: ${error.message}`);
    }
  },

  async getStorageStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Backend storage stats:', result);
        return result;
      } else {
        throw new Error(`Backend stats failed: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error getting storage stats from backend:', error);
      throw new Error(`Backend stats unavailable: ${error.message}`);
    }
  },

  // Critical data storage (localStorage fallback)
  storeCriticalData(key, data) {
    try {
      const serializedData = JSON.stringify(data);
      localStorage.setItem(key, serializedData);
      localStorage.setItem(`${key}_timestamp`, Date.now().toString());
      console.log(`Critical data stored for key: ${key}`);
    } catch (error) {
      console.error(`Error storing critical data for key ${key}:`, error);
    }
  },

  getCriticalData(key) {
    try {
      const data = localStorage.getItem(key);
      const timestamp = localStorage.getItem(`${key}_timestamp`);

      if (data && timestamp) {
        const parsedData = JSON.parse(data);
        console.log(`Critical data retrieved for key: ${key}`);
        return parsedData;
      }
      return null;
    } catch (error) {
      console.error(`Error getting critical data for key ${key}:`, error);
      return null;
    }
  },

  // Legacy localStorage methods (for fallback and critical state)
  isDataTooLarge: (data) => {
    try {
      const stringData = JSON.stringify(data);
      return stringData.length > storageManager.MAX_ITEM_SIZE;
    } catch (error) {
      console.error('Error checking data size:', error);
      return true;
    }
  },

  getStorageUsage: () => {
    try {
      let totalSize = 0;
      const keys = Object.keys(localStorage);

      for (const key of keys) {
        if (key.startsWith('queryResults_') ||
            key.startsWith('filteredResults_') ||
            key.startsWith('evaluationResults_') ||
            key.startsWith('callResults_')) {
          const item = localStorage.getItem(key);
          if (item) {
            totalSize += item.length;
          }
        }
      }

      return totalSize;
    } catch (error) {
      console.error('Error calculating storage usage:', error);
      return 0;
    }
  },

  clearOldData: () => {
    try {
      const keys = Object.keys(localStorage);
      const items = [];

      // Collect all storage items with timestamps
      for (const key of keys) {
        if (key.startsWith('queryResults_') ||
            key.startsWith('filteredResults_') ||
            key.startsWith('evaluationResults_') ||
            key.startsWith('callResults_')) {
          const timestamp = localStorage.getItem(`${key}_timestamp`);
          const data = localStorage.getItem(key);
          if (data) {
            items.push({
              key: key,
              timestamp: timestamp ? parseInt(timestamp) : 0,
              size: data.length
            });
          }
        }
      }

      // Sort by timestamp (oldest first)
      items.sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest items until we're under 80% capacity
      const targetUsage = storageManager.MAX_TOTAL_STORAGE * 0.8;
      let currentUsage = storageManager.getStorageUsage();

      for (const item of items) {
        if (currentUsage <= targetUsage) break;

        localStorage.removeItem(item.key);
        localStorage.removeItem(`${item.key}_timestamp`);
        currentUsage -= item.size;
        console.log(`Cleared old data: ${item.key}`);
      }
    } catch (error) {
      console.error('Error clearing old data:', error);
    }
  },

  // Compress data before storing
  compressData: (data) => {
    try {
      const jsonString = JSON.stringify(data);

      // Check if TextEncoder is available (modern browsers)
      if (typeof TextEncoder !== 'undefined') {
        // Convert string to UTF-8 bytes
        const utf8Bytes = new TextEncoder().encode(jsonString);

        // Convert bytes to binary string for btoa
        let binaryString = '';
        for (let i = 0; i < utf8Bytes.length; i++) {
          binaryString += String.fromCharCode(utf8Bytes[i]);
        }

        // Encode to base64
        return btoa(binaryString);
      } else {
        // Fallback for older browsers - encode Unicode characters
        return btoa(encodeURIComponent(jsonString));
      }
    } catch (error) {
      console.error('Error compressing data:', error);
      return null;
    }
  },

  decompressData: (compressedData) => {
    try {
      // Validate input
      if (!compressedData || typeof compressedData !== 'string') {
        console.warn('Invalid compressed data format');
        return null;
      }

      // Check if data looks like valid base64
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(compressedData)) {
        console.warn('Data is not valid base64 format, clearing corrupted data');
        return null;
      }

      // Decode base64 to binary string
      const binaryString = atob(compressedData);

      // Check if TextDecoder is available (modern browsers)
      if (typeof TextDecoder !== 'undefined') {
        // Convert binary string to UTF-8 bytes
        const utf8Bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          utf8Bytes[i] = binaryString.charCodeAt(i);
        }

        // Decode UTF-8 bytes to string
        const stringData = new TextDecoder().decode(utf8Bytes);
        return JSON.parse(stringData);
      } else {
        // Fallback for older browsers - unescape Unicode characters
        const stringData = decodeURIComponent(binaryString);
        return JSON.parse(stringData);
      }
    } catch (error) {
      console.error('Error decompressing data:', error);
      return null;
    }
  },

  // Store data with size checks and cleanup (localStorage fallback)
  storeData: (key, data) => {
    if (!key || !data) return false;

    // Check if data is too large
    if (storageManager.isDataTooLarge(data)) {
      console.warn(`Data too large to store in localStorage for key: ${key}`);
      return false;
    }

    try {
      // Check current storage usage
      const currentUsage = storageManager.getStorageUsage();
      if (currentUsage > storageManager.MAX_TOTAL_STORAGE) {
        console.log('Storage quota exceeded, cleaning up old data...');
        storageManager.clearOldData();
      }

      // Compress the data before storing
      const compressedData = storageManager.compressData(data);
      if (compressedData) {
        // Check if compressed data is still too large
        if (compressedData.length > storageManager.MAX_ITEM_SIZE) {
          console.warn(`Compressed data still too large to store for key: ${key}`);
          return false;
        }

        localStorage.setItem(key, compressedData);
        localStorage.setItem(`${key}_timestamp`, Date.now().toString());
        console.log(`Successfully stored data for key: ${key}`);
        return true;
      }
      return false;
    } catch (error) {
      console.warn(`Unable to store data for key ${key}:`, error.message);
      // If storage fails, try to clear old data and retry once
      storageManager.clearOldData();
      try {
        const compressedData = storageManager.compressData(data);
        if (compressedData && compressedData.length <= storageManager.MAX_ITEM_SIZE) {
          localStorage.setItem(key, compressedData);
          localStorage.setItem(`${key}_timestamp`, Date.now().toString());
          console.log(`Successfully stored data after cleanup for key: ${key}`);
          return true;
        }
      } catch (retryError) {
        console.warn(`Unable to store data for key ${key} - storage quota exceeded`);
        // Don't show alert - fail silently for large datasets
        return false;
      }
      return false;
    }
  },

  // Get stored data (localStorage fallback)
  getData: (key) => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const decompressed = storageManager.decompressData(stored);
      if (decompressed === null) {
        // Clear corrupted data
        console.warn(`Clearing corrupted data for key: ${key}`);
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}_timestamp`);
        return null;
      }

      return decompressed;
    } catch (error) {
      console.error(`Error getting data for key ${key}:`, error);
      // Clear potentially corrupted data
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_timestamp`);
      return null;
    }
  },

  // Legacy methods for backward compatibility (proper wrappers to avoid recursion)
  storeResults: async (jobId, results) => {
    try {
      return await storageManager.storeQueryResults(jobId, results);
    } catch (error) {
      // Fail silently - don't spam console with errors
      console.warn(`Backend storage unavailable for job ${jobId}, using localStorage fallback`);
      // Try localStorage fallback
      const sanitizedResults = storageManager.sanitizeResultsForStorage(results);
      storageManager.storeData(`queryResults_${jobId}`, sanitizedResults);
      return { success: true, message: 'Stored locally due to backend error' };
    }
  },

  storeFilteredResults: (jobId, results) => {
    // Only store in localStorage if the data is small enough
    // Large datasets should only be stored in backend
    const dataSize = JSON.stringify(results).length;
    const maxSize = 1 * 1024 * 1024; // 1MB limit for localStorage

    if (dataSize < maxSize) {
      try {
        storageManager.storeData(`filteredResults_${jobId}`, results);
        console.log(`Filtered results stored locally (${(dataSize / 1024).toFixed(2)} KB)`);
      } catch (error) {
        console.warn(`Skipping localStorage storage for job ${jobId} - data too large or quota exceeded`);
        // Don't show error to user - this is expected for large datasets
      }
    } else {
      console.log(`Skipping localStorage for job ${jobId} - data too large (${(dataSize / 1024 / 1024).toFixed(2)} MB)`);
    }
  },


  getResults: async (jobId) => {
    return await storageManager.getQueryResults(jobId);
  },

  getFilteredResults: (jobId) => {
    return storageManager.getData(`filteredResults_${jobId}`);
  },

  // Clear specific job data from localStorage only (not backend)
  clearLocalJobData: (jobId) => {
    localStorage.removeItem(`queryResults_${jobId}`);
    localStorage.removeItem(`queryResults_${jobId}_timestamp`);
    localStorage.removeItem(`filteredResults_${jobId}`);
    localStorage.removeItem(`filteredResults_${jobId}_timestamp`);
    localStorage.removeItem(`evaluationResults_${jobId}`);
    localStorage.removeItem(`evaluationResults_${jobId}_timestamp`);
    localStorage.removeItem(`callResults_${jobId}`);
    localStorage.removeItem(`callResults_${jobId}_timestamp`);
  },

  // Clear all data
  clearAllData: () => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('queryResults_') ||
          key.startsWith('filteredResults_') ||
          key.startsWith('evaluationResults_') ||
          key.startsWith('callResults_')) {
        localStorage.removeItem(key);
      }
    });
  },

  // Show user-friendly quota exceeded message
  showQuotaExceededMessage: () => {
    const message = `
      Storage quota exceeded!

      To fix this:
      1. Clear your browser's localStorage data
      2. Or refresh the page and try again with fewer results

      You can also clear storage manually by running this in the browser console:
      localStorage.clear();
    `;

    console.warn(message);

    // Show alert to user
    if (typeof window !== 'undefined' && window.alert) {
      alert('Storage quota exceeded! Please clear your browser data or refresh the page.');
    }
  },

  // Emergency clear function for users
  emergencyClear: () => {
    try {
      const keysToRemove = [];
      const keys = Object.keys(localStorage);

      // Only clear app-related keys
      keys.forEach(key => {
        if (key.startsWith('queryResults_') ||
            key.startsWith('filteredResults_') ||
            key.startsWith('evaluationResults_') ||
            key.startsWith('callResults_')) {
          keysToRemove.push(key);
        }
      });

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}_timestamp`);
      });

      console.log(`Emergency clear completed - removed ${keysToRemove.length} items`);
      return keysToRemove.length;
    } catch (error) {
      console.error('Error during emergency clear:', error);
      return 0;
    }
  },

  // Quick clear for quota issues (can be called from console)
  clearQuotaData: () => {
    console.log('🧹 Clearing large data to free up storage...');
    const cleared = storageManager.emergencyClear();
    console.log(`✅ Cleared ${cleared} items. You can now continue working.`);
    return cleared;
  },

  // Clear corrupted data and provide recovery options
  clearCorruptedData: () => {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach(key => {
        if (key.startsWith('queryResults_') ||
            key.startsWith('filteredResults_') ||
            key.startsWith('evaluationResults_') ||
            key.startsWith('callResults_')) {
          try {
            // Try to decompress to see if it's corrupted
            const stored = localStorage.getItem(key);
            if (stored) {
              const decompressed = storageManager.decompressData(stored);
              if (decompressed === null) {
                localStorage.removeItem(key);
                localStorage.removeItem(`${key}_timestamp`);
                clearedCount++;
              }
            }
          } catch (error) {
            // Remove corrupted data
            localStorage.removeItem(key);
            localStorage.removeItem(`${key}_timestamp`);
            clearedCount++;
          }
        }
      });

      console.log(`Cleared ${clearedCount} corrupted storage items`);
      return clearedCount;
    } catch (error) {
      console.error('Error clearing corrupted data:', error);
      return 0;
    }
  },

  // Force clear all storage data to start fresh
  forceClearAllData: () => {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach(key => {
        if (key.startsWith('queryResults_') ||
            key.startsWith('filteredResults_') ||
            key.startsWith('evaluationResults_') ||
            key.startsWith('callResults_')) {
          localStorage.removeItem(key);
          localStorage.removeItem(`${key}_timestamp`);
          clearedCount++;
        }
      });

      console.log(`Force cleared ${clearedCount} storage items`);
      return clearedCount;
    } catch (error) {
      console.error('Error force clearing data:', error);
      return 0;
    }
  },

  // Backend cleanup operations (only for cleanup, not storage)
  async runBackendCleanup() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Backend cleanup completed:', result);
        return result;
      } else {
        throw new Error(`Backend cleanup failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error running backend cleanup:', error);
      throw error;
    }
  },

  async getBackendStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/storage/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        return result.stats || {};
      } else {
        throw new Error(`Failed to get backend stats: ${response.status}`);
      }
    } catch (error) {
      console.error('Error getting backend stats:', error);
      return {};
    }
  },

  // Debug function to identify corrupted storage
  debugStorage: () => {
    try {
      const keys = Object.keys(localStorage);
      const storageInfo = {
        totalKeys: keys.length,
        appKeys: [],
        corruptedKeys: [],
        otherKeys: []
      };

      keys.forEach(key => {
        if (key.startsWith('queryResults_') ||
            key.startsWith('filteredResults_') ||
            key.startsWith('evaluationResults_') ||
            key.startsWith('callResults_')) {

          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              // Try to decompress to see if it's corrupted
              const decompressed = storageManager.decompressData(stored);
              if (decompressed === null) {
                storageInfo.corruptedKeys.push({
                  key: key,
                  size: stored.length,
                  preview: stored.substring(0, 50) + '...'
                });
              } else {
                storageInfo.appKeys.push({
                  key: key,
                  size: stored.length,
                  dataType: typeof decompressed,
                  isArray: Array.isArray(decompressed),
                  itemCount: Array.isArray(decompressed) ? decompressed.length : 'N/A'
                });
              }
            } catch (error) {
              storageInfo.corruptedKeys.push({
                key: key,
                size: stored.length,
                error: error.message,
                preview: stored.substring(0, 50) + '...'
              });
            }
          }
        } else {
          storageInfo.otherKeys.push(key);
        }
      });

      console.log('=== STORAGE DEBUG INFO ===');
      console.log(`Total localStorage keys: ${storageInfo.totalKeys}`);
      console.log(`App-related keys: ${storageInfo.appKeys.length}`);
      console.log(`Corrupted keys: ${storageInfo.corruptedKeys.length}`);
      console.log(`Other keys: ${storageInfo.otherKeys.length}`);

      if (storageInfo.corruptedKeys.length > 0) {
        console.log('\n=== CORRUPTED KEYS ===');
        storageInfo.corruptedKeys.forEach(item => {
          console.log(`- ${item.key}: ${item.size} chars, preview: ${item.preview}`);
          if (item.error) console.log(`  Error: ${item.error}`);
        });
      }

      if (storageInfo.appKeys.length > 0) {
        console.log('\n=== VALID APP KEYS ===');
        storageInfo.appKeys.forEach(item => {
          console.log(`- ${item.key}: ${item.size} chars, type: ${item.dataType}, items: ${item.itemCount}`);
        });
      }

      return storageInfo;
    } catch (error) {
      console.error('Error debugging storage:', error);
      return null;
    }
  },

  // Debug backend storage function
  async debugBackendStorage() {
    try {
      const stats = await this.getStorageStats();
      const localStorageKeys = Object.keys(localStorage);

      console.log('=== Storage Debug Information ===');
      console.log('Backend Storage Stats:', stats);
      console.log('localStorage Keys:', localStorageKeys);
      console.log('localStorage Usage:', this.getStorageUsage());
      console.log('================================');

      return {
        backend: stats,
        localStorage: {
          keys: localStorageKeys,
          usage: this.getStorageUsage()
        }
      };
    } catch (error) {
      console.error('Debug storage failed:', error);
      return { error: error.message };
    }
  }
};

export default storageManager;
